import React, {
  useCallback,
  useContext,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';

import { TabPanel, useTabStore } from '@ariakit/react';
import classNames from 'classnames';
import Highlight, { defaultProps } from 'prism-react-renderer';

import { CodeBlock } from '../../../../types';
import { CodeHighlightContext } from '../../../context/CodeHighlightContext';
import { InteractiveCodeContext } from '../../../context/InteractiveCodeContext';
import { useScrollIntoView } from '../../../hooks/useScrollIntoView';
import { isFirstActiveLine, isLineActive } from '../../../utils/codeBlock';
import { getCodeBlockId } from '../../../utils/codeBlock/getCodeBlockId';
import { CodeBlockContainer } from '../CodeBlockContainer';
import { CodeBlockLine } from '../CodeBlockLine';
import { CodePre } from '../CodePre';

import { InteractiveHeader } from './InteractiveHeader';
import * as classes from './style.module.scss';

interface Props {
  codeBlocks: CodeBlock[];
}

export const Interactive: React.FC<Props> = ({ codeBlocks }) => {
  const codeRefs = useRef<(HTMLDivElement | null)[]>(Array(codeBlocks.length));
  const timeoutRef = useRef<number | null>(null);

  const [mouseOver, setMouseOver] = useState(false);

  const scrollIntoView = useScrollIntoView();

  const { activeLines } = useContext(CodeHighlightContext);
  const { files, codeBlockIndex, setCodeBlockIndex } = useContext(
    InteractiveCodeContext,
  );

  // get all CodeBlockLines and add or remove classnames for highlighting
  useLayoutEffect(() => {
    const lines = document.querySelectorAll(
      `[data-number][data-file="${codeBlockIndex}"][data-allow-highlighting="true"]`,
    );

    lines.forEach((line, index) => {
      const active = isLineActive(activeLines, index);
      if (active) {
        line.classList.add(classes.highlight);
        line.classList.remove(classes.hide);

        // scroll the content of the CodeBlock
        if (isFirstActiveLine(activeLines, index)) {
          // note: despite useLayoutEffect was bounding rect empty from time to time
          timeoutRef.current = window.setTimeout(() => {
            scrollIntoView({
              target: line,
              scrollableRef: codeRefs.current[codeBlockIndex],
            });
          }, 0);
        }
      } else if (activeLines.length) {
        line.classList.add(classes.hide);
        line.classList.remove(classes.highlight);
      } else {
        line.classList.remove(classes.highlight);
        line.classList.remove(classes.hide);
      }
    });

    return () => {
      if (timeoutRef.current) {
        window.clearTimeout(timeoutRef.current);
      }
    };
  }, [activeLines, codeBlockIndex, codeRefs]);

  const handleMouseEnter = useCallback(() => {
    setMouseOver(true);
  }, []);

  const handleMouseLeave = useCallback(() => {
    setMouseOver(false);
  }, []);

  const handleSetSelectId = (id: string | null | undefined) => {
    const index = id?.split('-')[1];

    if (index) {
      setCodeBlockIndex(parseInt(index, 10));
    }
  };

  const ids = files?.map((file, i) => getCodeBlockId(file, i)) || [];

  const selectedId = ids.find(
    id => id.split('-')[1] === codeBlockIndex.toString(),
  );

  const tab = useTabStore({
    selectedId,
    setSelectedId: handleSetSelectId,
  });

  return (
    <CodeBlockContainer className={classes.root}>
      <InteractiveHeader tab={tab} />
      <CodePre
        codeLanguage={codeBlocks[codeBlockIndex]?.language || ''}
        codeBlockIndex={codeBlockIndex}
        className={classNames(classes.pre, {
          [classes.mouseover]: mouseOver,
        })}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {codeBlocks.map(({ code, title, language }, index) => (
          <TabPanel
            ref={element => {
              codeRefs.current[index] = element;
            }}
            key={title}
            className={classes.panel}
            store={tab}
          >
            {codeBlockIndex === index && (
              <Highlight
                {...defaultProps}
                theme={undefined}
                code={code.trimEnd().replace(/\n+$/, '')}
                // note: Language in Highlight does not work with our custom languages we added in Layout.tsx
                language={language as any}
              >
                {({ tokens, getLineProps, getTokenProps }) => {
                  return tokens.map((line, i) => (
                    <CodeBlockLine
                      // eslint-disable-next-line react/no-array-index-key
                      key={`${title}-${i}`}
                      index={i}
                      line={line}
                      file={index}
                      allowHighlighting
                      getLineProps={getLineProps}
                      getTokenProps={getTokenProps}
                    />
                  ));
                }}
              </Highlight>
            )}
          </TabPanel>
        ))}
      </CodePre>
    </CodeBlockContainer>
  );
};
